import { ComponentStore } from '@ngrx/component-store';
import { MenuItem } from 'primeng/api';
import {
  ILdCoordinatorState,
  initialLdCoordinatorState,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.state';
import { inject, Injectable } from '@angular/core';
import { LdTabs } from '@app/main/leak-detection/enums/ld-tabs';
import { concatMap, filter, shareReplay, take } from 'rxjs/operators';
import { mapObjectBy } from '@app/shared/utils/array-mappers';
import {
  LdActionPlace,
  LdActionType,
  LdCoordinatorActionCellIdClick,
  LdCoordinatorActionCellIdLoggerClick,
  LdCoordinatorActionCellIdVisibilityClick,
  LdCoordinatorActionChangedMapBounds,
  LdCoordinatorActionCorrelationClick,
  LdCoordinatorActionCorrelationContextMenu,
  LdCoordinatorActionCorrelationCtrlClick,
  LdCoordinatorActionCorrelationDbClick,
  LdCoordinatorActionEventClick,
  LdCoordinatorActionEventContextMenu,
  LdCoordinatorActionEventDbClick,
  LdCoordinatorActionLoggerClick,
  LdCoordinatorActionLoggerContextMenu,
  LdCoordinatorActionLoggerCtrlClick,
  LdCoordinatorActionLoggerDbClick,
  LdCoordinatorActionMapAreaContextMenu,
  LdCoordinatorActionPipeContextMenu,
  LdCoordinatorActions,
  LdCoordinatorActionShowInStreetView,
  LdCoordinatorActionShowPhotos,
  LdCoordinatorActionTabChange,
  LdCoordinatorActionZoomToFitItem,
  LdCoordinatorDispatch,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';
import { IEvent } from '@app/api/interfaces/event.interface';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { map, Observable, of } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { FpCorrelationService } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/services/fp-correlation.service';
import { FpLoggerNoiseService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/services/fp-logger-noise.service';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { DialogEventService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event.service';
import { FpLoggerNoiseTabs } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/enums/fp-logger-noise-tabs.enum';
import { setLeakDetectionPanelTab } from '@app/main/leak-detection/store/leak-detection-selection/leak-detection-selection.actions';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { Store } from '@ngrx/store';
import { ILdLogger, LdLoggerId } from '@app/main/leak-detection/interfaces/ld-logger';
import { LdDataAggregationService } from '@app/main/leak-detection/services/leak-detection-data-aggregation/leak-detection-data-aggregation.service';
import { ContextMenuService } from '@app/main/main-content/context-menu/services/context-menu.service';
import { LdContextMenuLoggerService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-logger.service';
import { LdContextMenuCorrelationService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-correlation.service';
import { LdContextMenuEventService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-event.service';
import { LdContextMenuMapAreaService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-map-area.service';
import { LdContextMenuPipeService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-pipe.service';

@Injectable({ providedIn: 'root' })
export class LdCoordinatorStore
  extends ComponentStore<ILdCoordinatorState>
  implements IGcComponentStore<LdCoordinatorActions>
{
  protected readonly store = inject(Store);
  private floatingPopupLoggerNoiseService = inject(FpLoggerNoiseService);
  private floatingPopupCorrelationService = inject(FpCorrelationService);
  private dialogEventService = inject(DialogEventService);
  private floatingPopupService = inject(FloatingPopupService);
  private ldDataAggregationService = inject(LdDataAggregationService);
  private contextMenuService = inject(ContextMenuService);
  private ldContextMenuLoggerService = inject(LdContextMenuLoggerService);
  private ldContextMenuCorrelationService = inject(LdContextMenuCorrelationService);
  private ldContextMenuEventService = inject(LdContextMenuEventService);
  private ldContextMenuMapAreaService = inject(LdContextMenuMapAreaService);
  private ldContextMenuPipeService = inject(LdContextMenuPipeService);

  constructor() {
    super(initialLdCoordinatorState);
  }

  public readonly selectedLoggers$ = this.select(s => s.selectedLoggers, { equal });
  public readonly selectedCorrelations$ = this.select(s => s.selectedCorrelations, { equal });
  public readonly selectedEvents$ = this.select(s => s.selectedEvents, { equal });
  public readonly selectionChanged$ = this.select(
    {
      loggers: this.selectedLoggers$,
      correlations: this.selectedCorrelations$,
      events: this.selectedEvents$,
    },
    { debounce: true }
  );

  public readonly activeTab$ = this.select(s => s.activeTab, { equal }).pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );
  public readonly scrollToId$ = this.select(s => s.scrollToId).pipe(filter((id): id is string => !!id));
  public readonly lastActionType$ = this.select(s => s.lastActionType, { equal });
  public readonly lastActionPlace$ = this.select(s => s.lastActionPlace, { equal });
  public readonly visibleCellsIds$ = this.select(s => s.visibleCellsIds, { equal });
  public readonly selectedCellsIds$ = this.select(s => s.selectedCellsIds, { equal });
  public readonly cellIdSelectionFromMap$ = this.select(s => s.cellIdSelectionFromMap, { equal });
  public readonly mapBounds$ = this.select(s => s.mapBounds, { equal });
  public readonly zoomToFitItemAction$ = this.select(s => s.actionZoomToFitItem).pipe(
    filter((action): action is LdCoordinatorActionZoomToFitItem => !!action)
  );
  public readonly showInStreetViewAction$ = this.select(s => s.actionShowInStreetView).pipe(
    filter((action): action is LdCoordinatorActionShowInStreetView => !!action)
  );

  public readonly cellIdSelectionFromMapS = toSignal(this.cellIdSelectionFromMap$, {
    initialValue: false,
  });

  public readonly visibleCellsIdsMapS = toSignal(
    this.visibleCellsIds$.pipe(map(ids => (ids ? mapObjectBy(ids, id => id) : null))),
    {
      initialValue: null,
    }
  );
  public readonly selectedCellsIdsMapS = toSignal(this.selectedCellsIds$.pipe(map(ids => mapObjectBy(ids, id => id))), {
    initialValue: {} as Record<string, string>,
  });
  public readonly selectedLoggersMapS = toSignal(
    this.selectedLoggers$.pipe(map(list => mapObjectBy(list, i => i.id))),
    {
      initialValue: {} as Record<LdLoggerId, ILdLogger>,
    }
  );
  public readonly selectedCorrelationsMapS = toSignal(
    this.selectedCorrelations$.pipe(map(list => mapObjectBy(list, i => i.id))),
    { initialValue: {} as Record<string, ICorrelation> }
  );
  public readonly selectedEventsMapS = toSignal(this.selectedEvents$.pipe(map(list => mapObjectBy(list, i => i.id))), {
    initialValue: {} as Record<string, IEvent>,
  });

  public dispatch = this.effect((action$: Observable<LdCoordinatorActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case LdActionType.ResetSelection: {
            this.reducerResetSelection();
            break;
          }
          case LdActionType.TabChange: {
            this.reducerTabChange(action);
            this.effectTabChange(action);
            break;
          }
          case LdActionType.LoggerClick: {
            this.reducerLoggerClick(action);
            break;
          }
          case LdActionType.LoggerCtrlClick: {
            this.reducerLoggerCtrlClick(action);
            break;
          }
          case LdActionType.LoggerDbClick: {
            this.reducerLoggerDbClick(action);
            this.effectLoggerDbClick(action);
            break;
          }
          case LdActionType.CellIdClick: {
            this.reducerCellIdClick(action);
            break;
          }
          case LdActionType.CellIdLoggerClick: {
            this.reducerCellIdLoggerClick(action);
            break;
          }
          case LdActionType.CellIdVisibilityClick: {
            this.reducerCellIdVisibilityClick(action);
            break;
          }
          case LdActionType.CorrelationClick: {
            this.reducerCorrelationClick(action);
            break;
          }
          case LdActionType.CorrelationCtrlClick: {
            this.reducerCorrelationCtrlClick(action);
            break;
          }
          case LdActionType.CorrelationDbClick: {
            this.reducerCorrelationDbClick(action);
            this.effectCorrelationDbClick(action);
            break;
          }
          case LdActionType.EventClick: {
            this.reducerEventClick(action);
            break;
          }
          case LdActionType.EventDbClick: {
            this.effectEventDbClick(action);
            break;
          }
          case LdActionType.ZoomToFitItem: {
            this.reducerZoomToFitItem(action);
            break;
          }
          case LdActionType.ShowInStreetView: {
            this.reducerShowInStreetView(action);
            break;
          }
          case LdActionType.ShowPhotos: {
            this.effectShowPhotos(action);
            break;
          }
          case LdActionType.ChangedMapBounds: {
            this.reducerChangedBounds(action);
            break;
          }
          case LdActionType.LoggerContextMenu: {
            this.reducerLoggerContextMenu(action);
            this.effectLoggerContextMenu(action);
            break;
          }
          case LdActionType.CorrelationContextMenu: {
            this.reducerCorrelationContextMenu(action);
            this.effectCorrelationContextMenu(action);
            break;
          }
          case LdActionType.EventContextMenu: {
            this.reducerEventContextMenu(action);
            this.effectEventContextMenu(action);
            break;
          }
          case LdActionType.MapAreaContextMenu: {
            this.effectMapAreaContextMenu(action);
            break;
          }
          case LdActionType.PipeContextMenu: {
            this.effectPipeContextMenu(action);
            break;
          }
        }

        return of(action);
      })
    )
  );

  private reducerResetSelection() {
    this.patchState({ ...initialLdCoordinatorState, activeTab: this.get().activeTab });
  }

  private reducerTabChange(action: LdCoordinatorActionTabChange) {
    let item: ICorrelation | ILdLogger | IEvent | null = null;
    const state = this.get();

    switch (action.tab) {
      case LdTabs.Correlations:
        item = Object.values(this.selectedCorrelationsMapS())[0];
        break;
      case LdTabs.LoggerNoise:
        item = Object.values(this.selectedLoggersMapS())[0];
        break;
      case LdTabs.Events:
        item = Object.values(this.selectedEventsMapS())[0];
        break;
    }

    this.patchState({
      activeTab: action.tab,
      scrollToId: item?.id ?? state.scrollToId,
      lastActionType: action.type,
      lastActionPlace: action.place,
    });
  }

  private effectTabChange(action: LdCoordinatorActionTabChange) {
    this.store.dispatch(setLeakDetectionPanelTab({ tab: action.tab }));
  }

  private reducerCellIdVisibilityClick(action: LdCoordinatorActionCellIdVisibilityClick): void {
    this.patchState({
      visibleCellsIds: action.items,
      lastActionType: action.type,
      lastActionPlace: action.place,
    });
  }

  private reducerCellIdClick(action: LdCoordinatorActionCellIdClick): void {
    this.ldDataAggregationService.ldLoggers$.pipe(take(1)).subscribe(loggers => {
      const item = action.item;
      const selecting = !this.selectedCellsIdsMapS()[item];
      const cellLoggers = loggers.filter((l): l is ILdLogger => !!l && l.deployment.servingCellId === item);
      this.patchState({
        selectedCellsIds: selecting ? [item] : [],
        cellIdSelectionFromMap: false,
        lastActionType: action.type,
        lastActionPlace: action.place,
        selectedLoggers: selecting ? cellLoggers : [],
        selectedCorrelations: [],
        selectedEvents: [],
        activeTab: LdTabs.LoggerNoise,
        scrollToId: selecting && cellLoggers.length ? cellLoggers[0].id : null,
      });
    });
  }

  private reducerCellIdLoggerClick(action: LdCoordinatorActionCellIdLoggerClick): void {
    const item = action.item;
    const selecting = !this.selectedLoggersMapS()[item.id];

    this.patchState({
      selectedCellsIds: [],
      cellIdSelectionFromMap: true,
      lastActionType: action.type,
      lastActionPlace: action.place,
      selectedLoggers: selecting ? [item] : [],
      selectedCorrelations: [],
      selectedEvents: [],
      activeTab: LdTabs.LoggerNoise,
      scrollToId: selecting ? item.id : null,
    });
  }

  private reducerChangedBounds(action: LdCoordinatorActionChangedMapBounds): void {
    this.patchState({
      mapBounds: action.bounds,
    });
  }

  private reducerLoggerClick(action: LdCoordinatorActionLoggerClick) {
    const state = this.get();

    const item = action.item;
    const isItemSelected = !!this.selectedLoggersMapS()[item.id];
    const actionPlace = action.place;
    const prevActiveTab = state.activeTab;
    const isTabNeedToUpdate =
      actionPlace === LdActionPlace.Map ||
      actionPlace === LdActionPlace.FloatingPopup ||
      actionPlace === LdActionPlace.Programmatic;
    const nextActiveTab = isTabNeedToUpdate ? LdTabs.LoggerNoise : prevActiveTab;
    const isSameTab = prevActiveTab === nextActiveTab;
    // FloatingPopup and Programmatic always select the item (never toggle it off).
    const isItemUnselectable =
      isSameTab && actionPlace !== LdActionPlace.FloatingPopup && actionPlace !== LdActionPlace.Programmatic;

    this.patchState({
      selectedCellsIds: [],
      selectedLoggers: isItemUnselectable ? (isItemSelected ? [] : [item]) : [item],
      activeTab: nextActiveTab,
      scrollToId: isTabNeedToUpdate ? (isItemUnselectable ? (isItemSelected ? null : item.id) : item.id) : null,
      lastActionType: action.type,
      lastActionPlace: actionPlace,
    });
  }

  private reducerLoggerCtrlClick(action: LdCoordinatorActionLoggerCtrlClick) {
    const state = this.get();

    const item = action.item;
    const isItemSelected = !!this.selectedLoggersMapS()[item.id];
    const actionPlace = action.place;
    const prevActiveTab = state.activeTab;
    const nextActiveTab = actionPlace === LdActionPlace.Map ? LdTabs.LoggerNoise : prevActiveTab;

    this.patchState({
      selectedCellsIds: [],
      selectedLoggers:
        prevActiveTab === nextActiveTab
          ? isItemSelected
            ? state.selectedLoggers.filter(logger => logger.id !== item.id)
            : [...state.selectedLoggers, item]
          : [item],
      activeTab: nextActiveTab,
      scrollToId:
        actionPlace === LdActionPlace.Map
          ? prevActiveTab === nextActiveTab
            ? isItemSelected
              ? null
              : item.id
            : item.id
          : null,
      lastActionType: action.type,
      lastActionPlace: actionPlace,
    });
  }

  private reducerLoggerDbClick(action: LdCoordinatorActionLoggerDbClick) {
    const state = this.get();

    const item = action.item;
    const actionPlace = action.place;
    const prevActiveTab = state.activeTab;
    const nextActiveTab = actionPlace === LdActionPlace.Map ? LdTabs.LoggerNoise : prevActiveTab;

    this.patchState({
      selectedCellsIds: [],
      selectedLoggers: [item],
      activeTab: nextActiveTab,
      scrollToId: actionPlace === LdActionPlace.Map ? item.id : null,
      lastActionType: action.type,
      lastActionPlace: actionPlace,
    });
  }

  private reducerCorrelationClick(action: LdCoordinatorActionCorrelationClick) {
    const state = this.get();

    const item = action.item;
    const isItemSelected = !!this.selectedCorrelationsMapS()[item.id];
    const actionPlace = action.place;
    const prevActiveTab = state.activeTab;
    const isTabNeedToUpdate = actionPlace === LdActionPlace.Map || actionPlace === LdActionPlace.FloatingPopup;
    const nextActiveTab = isTabNeedToUpdate ? LdTabs.Correlations : prevActiveTab;
    const isSameTab = prevActiveTab === nextActiveTab;
    const isItemUnselectable = isSameTab && actionPlace !== LdActionPlace.FloatingPopup;

    this.patchState({
      selectedCorrelations: isItemUnselectable ? (isItemSelected ? [] : [item]) : [item],
      activeTab: nextActiveTab,
      scrollToId: isTabNeedToUpdate ? (isItemUnselectable ? (isItemSelected ? null : item.id) : item.id) : null,
      lastActionType: action.type,
      lastActionPlace: actionPlace,
    });
  }

  private reducerCorrelationCtrlClick(action: LdCoordinatorActionCorrelationCtrlClick) {
    const state = this.get();

    const item = action.item;
    const isItemSelected = !!this.selectedCorrelationsMapS()[item.id];
    const actionPlace = action.place;
    const prevActiveTab = state.activeTab;
    const nextActiveTab = actionPlace === LdActionPlace.Map ? LdTabs.Correlations : prevActiveTab;

    this.patchState({
      selectedCorrelations:
        prevActiveTab === nextActiveTab
          ? isItemSelected
            ? state.selectedCorrelations.filter(logger => logger.id !== item.id)
            : [...state.selectedCorrelations, item]
          : [item],
      activeTab: nextActiveTab,
      scrollToId:
        actionPlace === LdActionPlace.Map
          ? prevActiveTab === nextActiveTab
            ? isItemSelected
              ? null
              : item.id
            : item.id
          : null,
      lastActionType: action.type,
      lastActionPlace: actionPlace,
    });
  }

  private reducerCorrelationDbClick(action: LdCoordinatorActionCorrelationDbClick) {
    const state = this.get();

    const item = action.item;
    const actionPlace = action.place;
    const prevActiveTab = state.activeTab;
    const nextActiveTab = actionPlace === LdActionPlace.Map ? LdTabs.Correlations : prevActiveTab;

    this.patchState({
      selectedCorrelations: [item],
      activeTab: nextActiveTab,
      scrollToId: actionPlace === LdActionPlace.Map ? item.id : null,
      lastActionType: action.type,
      lastActionPlace: actionPlace,
    });
  }

  private reducerEventClick(action: LdCoordinatorActionEventClick) {
    const state = this.get();

    const item = action.item;
    const isItemSelected = !!this.selectedEventsMapS()[item.id];
    const actionPlace = action.place;
    const prevActiveTab = state.activeTab;
    const nextActiveTab = actionPlace === LdActionPlace.Map ? LdTabs.Events : prevActiveTab;

    this.patchState({
      selectedEvents: prevActiveTab === nextActiveTab ? (isItemSelected ? [] : [item]) : [item],
      activeTab: nextActiveTab,
      scrollToId:
        actionPlace === LdActionPlace.Map
          ? prevActiveTab === nextActiveTab
            ? isItemSelected
              ? null
              : item.id
            : item.id
          : null,
      lastActionType: action.type,
      lastActionPlace: actionPlace,
    });
  }

  private effectLoggerDbClick(action: LdCoordinatorActionLoggerDbClick) {
    this.openLoggerNoisePopup(action.item);
  }

  private effectShowPhotos(action: LdCoordinatorActionShowPhotos) {
    this.openLoggerNoisePopup(action.item, FpLoggerNoiseTabs.Pictures);
  }

  private effectCorrelationDbClick(action: LdCoordinatorActionCorrelationDbClick) {
    const item = action.item;

    const runClickCorrelationAction = (isMobileMode: boolean) => {
      if (isMobileMode) {
        this.dispatch({
          type: LdActionType.CorrelationClick,
          place: LdActionPlace.FloatingPopup,
          item: item,
        });
      }
    };

    this.floatingPopupCorrelationService.open(item, {
      onOpen: (popupInstance: IFloatingPopupInstance, isMobileMode: boolean) => runClickCorrelationAction(isMobileMode),
      onMaximize: (popupInstance: IFloatingPopupInstance, isMobileMode: boolean) =>
        runClickCorrelationAction(isMobileMode),
      onSwitchToMobile: (popupInstance: IFloatingPopupInstance, isMobileMode: boolean) =>
        runClickCorrelationAction(isMobileMode),
    });
  }

  private effectEventDbClick(action: LdCoordinatorActionEventDbClick) {
    const item = action.item;

    this.dialogEventService.open(item);
  }

  private reducerZoomToFitItem(action: LdCoordinatorActionZoomToFitItem) {
    this.patchState({
      actionZoomToFitItem: action,
    });
  }

  // a new action object every time, so requesting the same position twice reaches the map twice
  private reducerShowInStreetView(action: LdCoordinatorActionShowInStreetView) {
    this.patchState({
      actionShowInStreetView: action,
    });
  }

  // --- context menus ---------------------------------------------------------
  // A right click makes the item the only selected one (it never toggles the selection off,
  // otherwise the menu would act on something that is no longer highlighted).

  private reducerLoggerContextMenu(action: LdCoordinatorActionLoggerContextMenu) {
    const isFromMap = action.place === LdActionPlace.Map;

    this.patchState({
      selectedCellsIds: [],
      selectedLoggers: [action.item],
      activeTab: isFromMap ? LdTabs.LoggerNoise : this.get().activeTab,
      scrollToId: isFromMap ? action.item.id : null,
      lastActionType: action.type,
      lastActionPlace: action.place,
    });
  }

  private reducerCorrelationContextMenu(action: LdCoordinatorActionCorrelationContextMenu) {
    const isFromMap = action.place === LdActionPlace.Map;

    this.patchState({
      selectedCorrelations: [action.item],
      activeTab: isFromMap ? LdTabs.Correlations : this.get().activeTab,
      scrollToId: isFromMap ? action.item.id : null,
      lastActionType: action.type,
      lastActionPlace: action.place,
    });
  }

  private reducerEventContextMenu(action: LdCoordinatorActionEventContextMenu) {
    const isFromMap = action.place === LdActionPlace.Map;

    this.patchState({
      selectedEvents: [action.item],
      activeTab: isFromMap ? LdTabs.Events : this.get().activeTab,
      scrollToId: isFromMap ? action.item.id : null,
      lastActionType: action.type,
      lastActionPlace: action.place,
    });
  }

  private effectLoggerContextMenu(action: LdCoordinatorActionLoggerContextMenu) {
    const service = this.ldContextMenuLoggerService;

    this.openContextMenu(action.event, dispatch =>
      action.place === LdActionPlace.Map
        ? service.getMapItems(action.item, dispatch)
        : service.getTableItems(action.item, action.cell, dispatch)
    );
  }

  private effectCorrelationContextMenu(action: LdCoordinatorActionCorrelationContextMenu) {
    const service = this.ldContextMenuCorrelationService;

    this.openContextMenu(action.event, dispatch =>
      action.place === LdActionPlace.Map
        ? service.getMapItems(action.item, dispatch)
        : service.getTableItems(action.item, action.cell, dispatch)
    );
  }

  private effectEventContextMenu(action: LdCoordinatorActionEventContextMenu) {
    const service = this.ldContextMenuEventService;

    this.openContextMenu(action.event, dispatch =>
      action.place === LdActionPlace.Map
        ? service.getMapItems(action.item, dispatch)
        : service.getTableItems(action.item, dispatch)
    );
  }

  private effectMapAreaContextMenu(action: LdCoordinatorActionMapAreaContextMenu) {
    // map only — the dispatcher is for `Create event`, which opens the event it made
    this.openContextMenu(action.event, dispatch =>
      this.ldContextMenuMapAreaService.getMapItems(action.position, dispatch)
    );
  }

  private effectPipeContextMenu(action: LdCoordinatorActionPipeContextMenu) {
    this.openContextMenu(action.event, () => this.ldContextMenuPipeService.getMapItems(action.item));
  }

  private openContextMenu(event: MouseEvent, getItems: (dispatch: LdCoordinatorDispatch) => MenuItem[]) {
    // config services get the real dispatcher, so a menu entry dispatches the action it means
    this.contextMenuService.open(event, getItems(this.dispatch));
  }

  private openLoggerNoisePopup(item: ILdLogger, selectedTab?: FpLoggerNoiseTabs) {
    const runClickLoggerAction = (isMobileMode: boolean) => {
      if (isMobileMode) {
        this.dispatch({
          type: LdActionType.LoggerClick,
          place: LdActionPlace.FloatingPopup,
          item,
        });
      }
    };

    this.floatingPopupLoggerNoiseService.open(
      item,
      {
        onOpen: (popupInstance: IFloatingPopupInstance, isMobileMode: boolean) => runClickLoggerAction(isMobileMode),
        onMaximize: (popupInstance: IFloatingPopupInstance, isMobileMode: boolean) =>
          runClickLoggerAction(isMobileMode),
        onSwitchToMobile: (popupInstance: IFloatingPopupInstance, isMobileMode: boolean) =>
          runClickLoggerAction(isMobileMode),
      },
      selectedTab
    );
  }
}
